import { useQuery } from "react-query";
import {
    Activity,
    AlertTriangle,
    BookLock,
    Crown,
    FolderGit2,
    GitBranch,
    Globe,
    ListChecks,
    Moon,
    ShieldAlert,
    ShieldOff,
    UserPlus,
    Users,
} from "lucide-react";
import { Card } from "../../../ui/card";
import { saasApi } from "../shared/saasApi";
import { EventCategory } from "../shared/saasTypes";
import {
    CATEGORY_COLORS,
    categoryLabel,
    findingLabel,
    formatDateTime,
    formatNumber,
    formatRelative,
    LOG_SOURCE_LABELS,
    PROVIDERS,
} from "../shared/saasFormat";
import { ActivityTimelineChart, SeverityDonut } from "../shared/SaasCharts";
import { BreakdownBars, HeroStat, MetaTag, SectionCard, StatusPill } from "../shared/SaasUi";
import { singleConnectionId, WorkspaceContext } from "./workspaceContext";

export default function OverviewTab({ context, onNavigate }: {
    context: WorkspaceContext;
    onNavigate: (tab: "identities" | "resources" | "findings" | "events" | "activity") => void;
}) {
    const meta = PROVIDERS[context.provider];
    const overviewQuery = useQuery(
        ["saas-overview", context.provider, context.tenantName, context.connectionId, context.range],
        () => saasApi.overview(context.provider, context.tenantName, context.connectionId, context.range),
        { keepPreviousData: true },
    );
    const activityQuery = useQuery(
        ["saas-activity", context.provider, context.tenantName, context.connectionId, context.range],
        () => saasApi.activity(context.provider, context.tenantName, context.connectionId, context.range),
        { keepPreviousData: true },
    );

    const overview = overviewQuery.data;
    const activity = activityQuery.data;
    const inventory = overview?.inventory;
    const resourceType = context.provider === "github" ? "REPOSITORY" : "PROJECT";
    const criticalHigh = (overview?.findings.bySeverity.CRITICAL ?? 0) + (overview?.findings.bySeverity.HIGH ?? 0);
    const topFindingTypes = Object.entries(overview?.findings.byType ?? {}).sort((left, right) => right[1] - left[1]).slice(0, 6);
    const connectionForIdentity = singleConnectionId(context);

    if (overviewQuery.isLoading || !overview) {
        return (
            <div className="space-y-6">
                <div className="cs-hero">
                    {[1, 2, 3, 4].map((index) => (
                        <div key={index} className="cs-hero-card animate-pulse h-[84px]" />
                    ))}
                </div>
                <Card className="cloud-card-surface rounded-2xl h-80 animate-pulse" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="cs-hero">
                <HeroStat icon={<Users />} value={formatNumber(inventory?.identities)} label="Identities" onClick={() => onNavigate("identities")} />
                <HeroStat icon={<Crown />} tone="amber" value={formatNumber(inventory?.privilegedIdentities)} label="Privileged identities"
                          onClick={() => onNavigate("identities")} />
                <HeroStat icon={<ShieldAlert />} tone="red" value={formatNumber(overview.findings.open)} label={`Open findings · ${formatNumber(criticalHigh)} critical/high`}
                          onClick={() => onNavigate("findings")} />
                <HeroStat icon={<Activity />} tone="green" value={formatNumber(overview.activity.eventCount)}
                          label={`Events · ${formatNumber(overview.activity.highRiskCount)} high risk`} onClick={() => onNavigate("events")} />
            </div>
            <div className="cs-hero">
                <HeroStat icon={<FolderGit2 />} value={formatNumber(inventory?.resourcesByType[resourceType] ?? 0)} label={meta.resourceLabel}
                          onClick={() => onNavigate("resources")} />
                <HeroStat icon={<Moon />} tone="amber" value={formatNumber(inventory?.dormantIdentities)}
                          label={`Dormant · ${formatNumber(inventory?.dormantPrivilegedIdentities)} privileged`} onClick={() => onNavigate("identities")} />
                <HeroStat icon={<Globe />} tone="red" value={formatNumber(inventory?.publicResources)} label="Public resources"
                          onClick={() => onNavigate("resources")} />
                <HeroStat icon={<GitBranch />} tone="red" value={formatNumber(inventory?.unprotectedDefaultBranches)} label="Unprotected default branches"
                          onClick={() => onNavigate("resources")} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <SectionCard
                    className="xl:col-span-2"
                    title="Activity"
                    subtitle={`${formatNumber(overview.activity.activeActors)} active identities across ${formatNumber(overview.activity.activeResources)} resources`}
                    icon={<Activity className="w-4 h-4" />}
                    actions={<button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => onNavigate("activity")}>View details</button>}
                >
                    {activity ? <ActivityTimelineChart activity={activity} /> : <div className="h-64 animate-pulse rounded-xl bg-primary/5" />}
                </SectionCard>
                <SectionCard
                    title="Open findings"
                    subtitle="Posture, least-privilege and activity alerts"
                    icon={<ListChecks className="w-4 h-4" />}
                    actions={<button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => onNavigate("findings")}>View all</button>}
                >
                    <SeverityDonut counts={overview.findings.bySeverity} total={overview.findings.open} label="open" />
                    <div className="mt-4 pt-4 border-t space-y-2">
                        {topFindingTypes.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No open findings</p>
                        ) : (
                            topFindingTypes.map(([type, count]) => (
                                <div key={type} className="flex items-center justify-between text-xs gap-3">
                                    <span className="truncate">{findingLabel(type)}</span>
                                    <span className="font-semibold">{formatNumber(count)}</span>
                                </div>
                            ))
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <SectionCard title="Most active identities" icon={<Users className="w-4 h-4" />} className="h-[380px]" scroll>
                    <BreakdownBars
                        items={activity?.actors ?? []}
                        showDistinct="resources"
                        onSelect={connectionForIdentity ? (item) => context.openIdentity(connectionForIdentity, item.key) : (item) => context.openEvents({ actor: item.key })}
                    />
                </SectionCard>
                <SectionCard title={`Most active ${meta.resourceLabel.toLowerCase()}`} icon={<BookLock className="w-4 h-4" />} className="h-[380px]" scroll>
                    <BreakdownBars items={activity?.resources ?? []} showDistinct="identities" onSelect={(item) => context.openEvents({ resource: item.key })} />
                </SectionCard>
                <SectionCard title="Activity by category" icon={<AlertTriangle className="w-4 h-4" />} className="h-[380px]" scroll>
                    <BreakdownBars
                        items={activity?.categories ?? []}
                        labelFor={(item) => categoryLabel(item.key)}
                        colorFor={(item) => CATEGORY_COLORS[item.key] ?? "hsl(var(--primary))"}
                        onSelect={(item) => context.openEvents({ category: item.key as EventCategory })}
                    />
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SectionCard title="Identity hygiene" icon={<UserPlus className="w-4 h-4" />}>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { label: "Outside collaborators", value: inventory?.outsideCollaborators },
                            { label: "Two-factor disabled", value: inventory?.twoFactorDisabled },
                            { label: "Bot accounts", value: inventory?.botIdentities },
                            { label: "Archived resources", value: inventory?.archivedResources },
                        ].map((item) => (
                            <div key={item.label} className="rounded-xl border border-border bg-card px-4 py-3 shadow-[0_2px_8px_rgb(15_23_42/0.04)]">
                                <div className="text-xl font-bold">{formatNumber(item.value)}</div>
                                <div className="text-xs text-muted-foreground">{item.label}</div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
                <SectionCard title="Log coverage" subtitle="Collection status per connected account" icon={<ShieldOff className="w-4 h-4" />} scroll className="max-h-[320px]">
                    <div className="space-y-2">
                        {overview.logCoverage.map((coverage) => {
                            const connection = context.connections.find((candidate) => candidate.id === coverage.connectionId);
                            const expired = coverage.credentialState === "EXPIRED";
                            const failed = Boolean(coverage.lastLogFetchError);
                            return (
                                <div key={coverage.connectionId} className="rounded-xl border border-border bg-card px-3 py-2.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium truncate">{coverage.account}</span>
                                        <StatusPill
                                            tone={expired ? "error" : failed ? "warning" : coverage.historicalCompletedAt ? "healthy" : "working"}
                                            label={expired ? "Reconnect required" : failed ? "Collection issue" : coverage.historicalCompletedAt ? "Collecting" : "Collecting history"}
                                            title={coverage.lastLogFetchError ?? connection?.credentialReason ?? undefined}
                                        />
                                        {coverage.logSource && <MetaTag>{LOG_SOURCE_LABELS[coverage.logSource]}</MetaTag>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Logs until {formatDateTime(coverage.logsCollectedUntil, "pending")} · last fetch {formatRelative(coverage.lastLogFetchAt)}
                                    </div>
                                    {coverage.lastLogFetchError && <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">{coverage.lastLogFetchError}</div>}
                                    {coverage.logSource === "EVENTS" && context.provider === "github" && (
                                        <div className="text-xs text-muted-foreground mt-1">
                                            The organization audit log requires GitHub Enterprise Cloud. Repository activity events are used instead.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </SectionCard>
            </div>
        </div>
    );
}
